/**
 * Per-order quantity limits (config: snippets/qty-limits.liquid -> window.qtyLimits,
 * keyed by product handle: { max, title }).
 *
 * - Product page and native cart page: quantity inputs carry data-qty-max (set in
 *   product-buttons.liquid / cart-line-items.liquid). Typed values are clamped and
 *   the + button is marked .is-qty-max once the field reaches the max. The
 *   "Max: N" note shows on the cart page only (the PDP relies on the modal).
 * - Rebuy Smart Cart: Rebuy renders its own items (li.product-<handle>), so the +
 *   button there is marked from here whenever the product's total reaches the max.
 *   The "Max: N" note is CSS-only (see the snippet) so Rebuy's Vue DOM isn't touched.
 * - A marked + looks disabled but stays focusable; clicking it opens a modal
 *   explaining the limit instead of adding more.
 * - Checkout: if the cart still holds more than the max (added twice from the PDP,
 *   an old cart, etc.), every checkout button in Rebuy and on the cart page is
 *   blocked with a modal that explains why and offers to lower the quantity.
 */
(function () {
  var limits = window.qtyLimits || {};
  if (!Object.keys(limits).length) return;

  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    '.rebuy-cart__checkout-button',
    '#nvd-with-checkout-btn',
    '#nvd-without-checkout-btn',
    'a[href^="/checkout"]',
    'a[href*="/checkouts/"]',
    '.additional-checkout-buttons',
    'shopify-accelerated-checkout-cart',
    '.shopify-payment-button',
  ].join(',');
  // Only cart-level buttons are guarded; the PDP's own Buy it now ignores the cart.
  var CART_CONTEXT_SELECTOR = '#rebuy-cart, [data-cart-form], .cart__buttons-wrapper, form[action$="/cart"]';

  var cachedItems = null;

  /* ---------- cart state ---------- */

  function refreshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { cachedItems = cart.items || []; return cachedItems; })
      .catch(function () { return cachedItems; });
  }

  function rebuyItems() {
    var cart = window.Rebuy && Rebuy.Cart && Rebuy.Cart.cart;
    return cart && Array.isArray(cart.items) ? cart.items : null;
  }

  // Totals per limited handle that go over their max, from whichever sources we have.
  function overLimits() {
    var totals = {};
    function add(source, handle, qty) {
      if (!limits[handle]) return;
      totals[source] = totals[source] || {};
      totals[source][handle] = (totals[source][handle] || 0) + qty;
    }

    (rebuyItems() || cachedItems || []).forEach(function (item) {
      add('cart', item.handle, Number(item.quantity) || 0);
    });
    rebuyLines().forEach(function (line) { add('rebuy', line.handle, line.qty); });
    document.querySelectorAll('input[data-qty-line]').forEach(function (input) {
      add('page', input.dataset.qtyHandle, parseInt(input.value, 10) || 0);
    });

    var over = {};
    Object.keys(totals).forEach(function (source) {
      Object.keys(totals[source]).forEach(function (handle) {
        var qty = totals[source][handle];
        if (qty > limits[handle].max && qty > (over[handle] || 0)) over[handle] = qty;
      });
    });
    return Object.keys(over).map(function (handle) {
      return { handle: handle, qty: over[handle], max: limits[handle].max, title: limits[handle].title };
    });
  }

  /* ---------- PDP + cart page inputs ---------- */

  // clamp only for the customer's own edits; on render the cart page field
  // should keep showing what's really in the cart.
  function syncInput(input, clamp) {
    var max = parseInt(input.dataset.qtyMax, 10);
    if (!max) return;
    var value = parseInt(input.value, 10);
    if (clamp && value > max) input.value = max;
    var counter = input.closest('quantity-counter');
    var plus = counter && counter.querySelector('button[name="increase"]');
    if (plus) markPlus(plus, (parseInt(input.value, 10) || 0) >= max, input.dataset.qtyHandle);
  }

  function markPlus(plus, atMax, handle) {
    plus.classList.toggle('is-qty-max', atMax);
    if (atMax) {
      plus.setAttribute('aria-disabled', 'true');
      plus.dataset.qtyLimitHandle = handle;
    } else if (plus.hasAttribute('aria-disabled')) {
      plus.removeAttribute('aria-disabled');
    }
  }

  function syncInputs() {
    document.querySelectorAll('input[data-qty-max]').forEach(function (input) { syncInput(input, false); });
  }

  // Capture phase so the value is clamped before the theme's own change handler
  // (the cart page sends updates[] straight to /cart/change.js).
  ['input', 'change'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var input = event.target;
      if (input && input.matches && input.matches('input[data-qty-max]')) syncInput(input, true);
    }, true);
  });

  /* ---------- Rebuy Smart Cart ---------- */

  function rebuyLines() {
    var lines = [];
    document.querySelectorAll('#rebuy-cart .rebuy-cart__flyout-item').forEach(function (li) {
      var handle = null;
      li.classList.forEach(function (cls) {
        if (cls.indexOf('product-') === 0 && limits[cls.slice(8)]) handle = cls.slice(8);
      });
      if (!handle) return;
      var label = li.querySelector('.rebuy-cart__flyout-item-quantity-widget-label');
      var qty = label ? parseInt(label.textContent.replace(/\D+/g, ' ').trim().split(' ').pop(), 10) || 0 : 0;
      lines.push({ li: li, handle: handle, qty: qty });
    });
    return lines;
  }

  function syncRebuy() {
    var lines = rebuyLines();
    var totals = {};
    lines.forEach(function (line) { totals[line.handle] = (totals[line.handle] || 0) + line.qty; });
    lines.forEach(function (line) {
      var buttons = line.li.querySelectorAll('.rebuy-cart__flyout-item-quantity-widget-button');
      var plus = line.li.querySelector('.rebuy-cart__flyout-item-quantity-widget-button[aria-label^="Increase"]') || buttons[buttons.length - 1];
      if (!plus) return;
      markPlus(plus, totals[line.handle] >= limits[line.handle].max, line.handle);
    });
  }

  /* ---------- checkout guard + modal ---------- */

  var dialog = null;

  function buildDialog() {
    dialog = document.createElement('dialog');
    dialog.className = 'qty-limit-modal';
    dialog.setAttribute('aria-labelledby', 'qty-limit-modal-title');
    dialog.innerHTML =
      '<button type="button" class="qty-limit-modal__close" data-qty-limit-close aria-label="Close">&times;</button>' +
      '<h2 class="qty-limit-modal__title" id="qty-limit-modal-title">Order limit reached</h2>' +
      '<div class="qty-limit-modal__body" data-qty-limit-body></div>' +
      '<div class="qty-limit-modal__actions">' +
      '<button type="button" class="btn btn--primary btn--solid btn--full" data-qty-limit-fix></button>' +
      '<button type="button" class="btn btn--primary btn--outline btn--full" data-qty-limit-close>Back to cart</button>' +
      '</div>';
    document.body.appendChild(dialog);

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.closest('[data-qty-limit-close]')) dialog.close();
      if (event.target.closest('[data-qty-limit-fix]')) fixCart(event.target.closest('[data-qty-limit-fix]'));
    });
  }

  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function showDialog(focusEl) {
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    focusEl.focus();
  }

  // Clicked + at the max: explain the limit, nothing to fix.
  function openLimitInfo(handle) {
    var limit = limits[handle];
    if (!limit) return;
    if (!dialog) buildDialog();
    dialog.querySelector('#qty-limit-modal-title').textContent = 'Limit ' + limit.max + ' per order';
    dialog.querySelector('[data-qty-limit-body]').innerHTML =
      '<p><strong>' + escapeHtml(limit.title) + '</strong> is a limited-edition item, so each order can include ' +
      (limit.max === 1 ? 'only 1' : 'up to ' + limit.max) + '.</p>';
    dialog.querySelector('[data-qty-limit-fix]').hidden = true;
    var close = dialog.querySelector('.qty-limit-modal__actions [data-qty-limit-close]');
    close.textContent = 'Got it';
    showDialog(close);
  }

  function openDialog(over) {
    if (!dialog) buildDialog();
    dialog.querySelector('#qty-limit-modal-title').textContent = 'Order limit reached';
    dialog.querySelector('.qty-limit-modal__actions [data-qty-limit-close]').textContent = 'Back to cart';
    dialog.querySelector('[data-qty-limit-body]').innerHTML = over.map(function (item) {
      return '<p><strong>' + escapeHtml(item.title) + '</strong> is limited to ' + item.max +
        ' per order, and your cart has ' + item.qty + '. Please lower the quantity to ' +
        (item.max === 1 ? '1' : item.max + ' or fewer') + ' to check out.</p>';
    }).join('');
    var fix = dialog.querySelector('[data-qty-limit-fix]');
    fix.hidden = false;
    fix.disabled = false;
    fix.textContent = over.length === 1 ? 'Update to ' + over[0].max : 'Update quantities';
    showDialog(fix);
  }

  // Lower each limited product to its max (keeping the earliest lines first).
  function fixCart(button) {
    button.disabled = true;
    refreshCart().then(function (items) {
      var allowance = {};
      var changes = [];
      (items || []).forEach(function (item) {
        var limit = limits[item.handle];
        if (!limit) return;
        if (!(item.handle in allowance)) allowance[item.handle] = limit.max;
        var keep = Math.min(item.quantity, allowance[item.handle]);
        allowance[item.handle] -= keep;
        if (keep !== item.quantity) changes.push({ id: item.key, quantity: keep });
      });
      return changes.reduce(function (chain, change) {
        return chain.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(change),
          });
        });
      }, Promise.resolve());
    }).then(function () {
      if (document.querySelector('[data-cart-form]')) {
        window.location.reload();
        return;
      }
      var done = window.Rebuy && Rebuy.Cart && Rebuy.Cart.fetchCart ? Promise.resolve(Rebuy.Cart.fetchCart()) : Promise.resolve();
      return done.then(refreshCart).then(function () { dialog.close(); });
    }).catch(function () {
      button.disabled = false;
    });
  }

  function guard(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var maxedPlus = event.type === 'click' && target.closest('.is-qty-max');
    if (maxedPlus) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openLimitInfo(maxedPlus.dataset.qtyLimitHandle);
      return;
    }
    var trigger = event.type === 'submit' ? event.submitter : target.closest(CHECKOUT_SELECTOR);
    if (event.type === 'submit') {
      var form = target;
      var toCheckout = (trigger && trigger.name === 'checkout') || /\/checkout/.test(form.getAttribute('action') || '');
      if (!toCheckout) return;
      trigger = trigger || form;
    }
    if (!trigger || trigger.name === 'update' || !trigger.closest(CART_CONTEXT_SELECTOR)) return;

    var over = overLimits();
    if (!over.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openDialog(over);
  }

  // Window capture runs before Rebuy's and Navidium's own button handlers.
  window.addEventListener('click', guard, true);
  window.addEventListener('submit', guard, true);

  /* ---------- keeping in sync ---------- */

  var queued = false;
  function sync() {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      syncInputs();
      syncRebuy();
    }, 50);
  }

  // Note any cart write (theme, Rebuy, apps) so the cached cart stays current.
  var nativeFetch = window.fetch;
  window.fetch = function (resource) {
    var result = nativeFetch.apply(this, arguments);
    var url = typeof resource === 'string' ? resource : resource && resource.url;
    if (url && /\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(function () { refreshCart().then(sync); }, function () {});
    }
    return result;
  };

  document.addEventListener('rebuy:cart.change', function () { refreshCart().then(sync); });

  function init() {
    refreshCart();
    sync();
    new MutationObserver(sync).observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
